import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, Upload, Row, Col,
  message, Space, Tag, Popconfirm, Alert, Spin, Divider, Empty, Select
} from 'antd';
import { PlusOutlined, SearchOutlined, UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  createProject,
  fetchProjects,
  deleteProject,
  updateProject,
  setHomepageOrder
} from '../../store/features/dashboard/projectSlice';
import { S3_CONFIG } from '../../config/s3';
import { debounce, beforeUploadValidation } from '../../lib/utils';

const { TextArea } = Input;
const { Option } = Select;

const Projects = () => {
  const dispatch = useDispatch();
  const {
    projects = [],
    loading,
    error,
    success,
    total = 0,
    currentPage = 1,
    pageSize = 10,
  } = useSelector((state) => state.projects);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [form] = Form.useForm();
  const searchInputRef = useRef(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deletedFilter, setDeletedFilter] = useState('all'); // 'all', 'true', 'false'
  const [homepageFilter, setHomepageFilter] = useState('all'); // 'all', 'true', 'false'

  const handleOrderClick = (project) => {
    setSelectedProject({
      ...project,
      homepageOrder: project.homepage_order || 0
    });
    setOrderModalVisible(true);
  };

  const handleOrderUpdate = async () => {
    try {
      const existingProject = projects.find(s =>
        s.homepage_order === selectedProject.homepageOrder &&
        s.id !== selectedProject.id
      );

      if (existingProject && selectedProject.homepageOrder > 0) {
        Modal.confirm({
          title: 'Position Already Taken',
          content: `Position ${selectedProject.homepageOrder} is currently held by "${existingProject.title}". Do you want to replace it?`,
          onOk: async () => {
            try {
              await dispatch(setHomepageOrder({
                projectId: selectedProject.id,
                order: selectedProject.homepageOrder
              })).unwrap();

              message.success('Homepage positions updated!');
              fetchData(currentPage);
              setOrderModalVisible(false);
            } catch (err) {
              message.error(err.message || 'Failed to update positions');
            }
          }
        });
      } else {
        await dispatch(setHomepageOrder({
          projectId: selectedProject.id,
          order: selectedProject.homepageOrder
        })).unwrap();

        message.success(
          selectedProject.homepageOrder > 0
            ? `Project set to position ${selectedProject.homepageOrder}`
            : 'Project removed from homepage'
        );
        fetchData(currentPage);
        setOrderModalVisible(false);
      }
    } catch (err) {
      message.error(err.message || 'Failed to update position');
    }
  };

  const fetchData = useCallback((page, size, search = '', deleted = deletedFilter, homepage = homepageFilter) => {
    setIsSearching(true);
    dispatch(fetchProjects({
      page,
      limit: size || pageSize,
      search,
      deleted: deleted === 'true' ? 'true' : '', // Only send 'true' or empty string
      homepage: homepage === 'true' ? 'true' : '' // Only send 'true' or empty string
    })).finally(() => {
      setIsSearching(false);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  }, [dispatch, pageSize, deletedFilter, homepageFilter]);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue.length >= 2 || searchValue.length === 0) {
        fetchData(1, pageSize, searchValue);
      }
    }, 500),
    [fetchData, pageSize]
  );

  const handleSearch = (value, event) => {
    setSearchText(value);
    if (event && event.type === 'change') {
      debouncedSearch(value);
    } else {
      fetchData(1, pageSize, value);
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    fetchData(1, pageSize, '');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, searchText);
  }, [currentPage, pageSize, deletedFilter, homepageFilter]);

  const handleDelete = async (projectId) => {
    try {
      await dispatch(deleteProject(projectId));
      if (projects.length === 1 && currentPage > 1) {
        fetchData(currentPage - 1);
        message.success('Project soft deleted successfully');
      } else {
        fetchData(currentPage);
        message.success('Project soft deleted successfully');
      }
    } catch (error) {
      message.error('Failed to soft delete project');
    }
  };

  const handleEdit = (project) => {
    setIsEditMode(true);
    setCurrentProjectId(project.id);
    setIsModalVisible(true);

    const imageFields = {};
    if (Array.isArray(project.images)) {
      project.images.forEach((image) => {
        if (image && typeof image === 'object' && image.url && Number.isInteger(image.order)) {
          const imageKey = `image${image.order + 1}`;
          imageFields[imageKey] = [{
            uid: image.url,
            name: image.url.split('/').pop(),
            status: 'done',
            url: `${S3_CONFIG.publicUrl}/${image.url}`,
          }];
        }
      });
    }

    for (let i = 1; i <= 5; i++) {
      if (!imageFields[`image${i}`]) {
        imageFields[`image${i}`] = [];
      }
    }

    const factsFields = {};
    if (Array.isArray(project.facts)) {
      project.facts.forEach((fact, index) => {
        factsFields[`fact${index + 1}`] = fact;
      });
    }

    const resultsData = {
      result1Title: project.results?.[0]?.title || '',
      result1Description: project.results?.[0]?.description || '',
      result2Title: project.results?.[1]?.title || '',
      result2Description: project.results?.[1]?.description || '',
    };

    form.setFieldsValue({
      title: project.title || '',
      description: project.description || '',
      category: project.category || '',
      client: project.client || '',
      type: project.type || '',
      date: project.date || '',
      website: project.website || '',
      factsDescription: project.factsDescription || '',
      resultsDescription: project.resultsDescription || '',
      ...factsFields,
      ...resultsData,
      ...imageFields,
    });
  };

  const handleOk = () => {
    const values = form.getFieldsValue();

    const requiredFields = [
      { name: 'title', label: 'Title' },
      { name: 'description', label: 'Description' },
      { name: 'category', label: 'Category' },
      { name: 'client', label: 'Client' },
      { name: 'type', label: 'Type' },
      { name: 'date', label: 'Date' },
      { name: 'factsDescription', label: 'Facts Description' },
      { name: 'resultsDescription', label: 'Results Description' },
    ];

    const emptyFields = requiredFields.filter(field => {
      const value = values[field.name];
      return !value || (typeof value === 'string' && !value.trim());
    });

    if (emptyFields.length > 0) {
      emptyFields.forEach(field => {
        message.error(`Please fill the ${field.label} field`);
      });
      return;
    }

    const imageFields = ['image1', 'image2', 'image3', 'image4', 'image5'];
    const missingImages = imageFields.filter(field => !values[field] || values[field].length === 0);
    if (missingImages.length > 0) {
      message.error(`Please upload all 5 images (missing: ${missingImages.map(f => f.replace('image', 'Image ')).join(', ')})`);
      return;
    }

    if ((values.result1Title && !values.result1Description) || (!values.result1Title && values.result1Description)) {
      message.error('Please fill both First Result Title and Description or leave both empty');
      return;
    }
    if ((values.result2Title && !values.result2Description) || (!values.result2Title && values.result2Description)) {
      message.error('Please fill both Second Result Title and Description or leave both empty');
      return;
    }

    form.validateFields().then(async () => {
      try {
        const formData = new FormData();

        formData.append('title', values.title);
        formData.append('description', values.description);
        formData.append('category', values.category);
        formData.append('client', values.client);
        formData.append('type', values.type);
        formData.append('date', values.date);
        formData.append('website', values.website || '');
        formData.append('factsDescription', values.factsDescription);
        formData.append('resultsDescription', values.resultsDescription);

        const newFiles = [];
        const fileOrders = [];
        const existingImages = [];

        imageFields.forEach((fieldName, index) => {
          const fileList = values[fieldName];
          if (fileList && fileList[0]) {
            const fileInfo = fileList[0];
            const order = index;

            if (fileInfo.originFileObj) {
              newFiles.push(fileInfo.originFileObj);
              fileOrders.push(order);
            } else if (fileInfo.url) {
              const cleanUrl = fileInfo.url.replace(`${S3_CONFIG.publicUrl}/`, '');
              existingImages.push({ url: cleanUrl, order });
            }
          }
        });

        newFiles.forEach(file => {
          formData.append('files', file);
        });
        if (fileOrders.length > 0) {
          formData.append('fileOrders', JSON.stringify(fileOrders));
        }
        if (existingImages.length > 0) {
          formData.append('existingImagePositions', JSON.stringify(existingImages));
        }

        const facts = [];
        for (let i = 1; i <= 5; i++) {
          if (values[`fact${i}`]?.trim()) {
            facts.push(values[`fact${i}`].trim());
          }
        }
        formData.append('facts', JSON.stringify(facts));

        const results = [];
        if (values.result1Title?.trim() && values.result1Description?.trim()) {
          results.push({
            title: values.result1Title.trim(),
            description: values.result1Description.trim(),
          });
        }
        if (values.result2Title?.trim() && values.result2Description?.trim()) {
          results.push({
            title: values.result2Title.trim(),
            description: values.result2Description.trim(),
          });
        }
        formData.append('results', JSON.stringify(results));

        let response;
        if (isEditMode) {
          response = await dispatch(updateProject({ projectId: currentProjectId, formData }));
        } else {
          response = await dispatch(createProject(formData));
        }

        if (response.error) {
          throw new Error(response.payload?.message || `Failed to ${isEditMode ? 'update' : 'create'} project`);
        }

        message.success(isEditMode ? 'Project updated successfully' : 'Project created successfully');
        resetModal();
        await fetchData(currentPage);
      } catch (error) {
        console.error('Error details:', {
          message: error.message,
          response: error.response ? error.response.data : null,
          stack: error.stack,
        });
        message.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} project`);
      }
    }).catch((errorInfo) => {
      console.log('Validation failed:', errorInfo);
      message.error('Form validation failed');
    });
  };

  const resetModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setCurrentProjectId(null);
    setExistingImages([]);
    form.resetFields();
  };

  const handleCancel = () => {
    resetModal();
  };

  const handlePageChange = (page, size) => {
    fetchData(page, size);
  };

  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList;
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: category => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Homepage Position',
      dataIndex: 'homepage_order',
      key: 'homepage_order',
      width: 180,
      render: (order, record) => {
        const isConflict = projects.filter(s =>
          s.homepage_order === order && s.id !== record.id && order > 0
        ).length > 0;

        return (
          <Tag
            color={order > 0 ? (isConflict ? 'red' : 'green') : 'orange'}
            style={{ cursor: record.status === 'deleted' ? 'not-allowed' : 'pointer' }}
            onClick={() => record.status !== 'deleted' && handleOrderClick(record)}
          >
            {order > 0 ? `Position ${order}${isConflict ? ' (Conflict!)' : ''}` : 'Not on homepage'}
          </Tag>
        );
      }
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      width: 150,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            onClick={() => handleEdit(record)} 
            disabled={record.status === 'deleted'}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to soft delete this project?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.status === 'deleted'}
          >
            <Button 
              danger 
              disabled={record.status === 'deleted'}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && projects.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Projects"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    }}>
      <h2 style={{ marginBottom: '16px' }}>Projects Management</h2>

      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '16px'
      }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setIsEditMode(false);
                setIsModalVisible(true);
              }}
            >
              Add Project
            </Button>
          </Col>
          <Col>
            <Space>
              <Select
                value={deletedFilter}
                allowClear
                onChange={(value) => {
                  setDeletedFilter(value);
                  fetchData(1, pageSize, searchText, value, homepageFilter);
                }}
                style={{ width: 150 }}
              >
                <Option value="true">Deleted Only</Option>
              </Select>
              <Select
                value={homepageFilter}
                allowClear
                onChange={(value) => {
                  setHomepageFilter(value);
                  fetchData(1, pageSize, searchText, deletedFilter, value);
                }}
                style={{ width: 150 }}
              >
                <Option value="true">Homepage Only</Option>
              </Select>
              <Input.Search
                ref={searchInputRef}
                placeholder="Search projects..."
                allowClear
                enterButton={isSearching ? <LoadingOutlined /> : <SearchOutlined />}
                size="large"
                style={{ width: 300 }}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value, e)}
                onSearch={(value) => handleSearch(value, { type: 'search' })}
                onClear={handleClearSearch}
                loading={isSearching}
              />
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ width: '100%', maxWidth: '1200px' }}>
        {projects.length === 0 && !loading ? (
          <Empty
            description={
              searchText || deletedFilter !== 'all' || homepageFilter !== 'all'
                ? 'No projects found with the applied filters'
                : 'No projects available'
            }
            style={{ margin: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={projects}
            loading={loading}
            rowKey="id"
            bordered
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 340px)'
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '15', '20'],
              showTotal: (total, range) => (
                <span>
                  Showing {range[0]}-{range[1]} of {total} records
                </span>
              ),
              position: ['bottomCenter'],
            }}
            style={{ width: '100%' }}
          />
        )}
      </div>
      <Modal
        title={isEditMode ? 'Edit Project' : 'Add Project'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
        confirmLoading={loading}
      >
        <Divider orientation="left">please follow the limit of characters to maintain the design</Divider>
        <Form layout="vertical" form={form}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please input the title!' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description (3 lines)"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={3} placeholder="Description" />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please input the category!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="client" label="Client" rules={[{ required: true, message: 'Please input the client!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Please input the type!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Please select a date!' }]}>
            <Input type="date" />
          </Form.Item>
          <Form.Item name="website" label="Website">
            <Input />
          </Form.Item>
          <Form.Item
            name="factsDescription"
            label="Facts Description (4 lines)"
            rules={[{ required: true, message: 'Please enter facts description' }]}
          >
            <TextArea rows={3} placeholder="Facts description" />
          </Form.Item>
          {[1, 2, 3, 4].map((num) => (
            <Form.Item
              key={`fact${num}`}
              name={`fact${num}`}
              label={`Fact ${num}`}
            >
              <Input placeholder={`Enter fact ${num}`} />
            </Form.Item>
          ))}
          <Form.Item
            name="resultsDescription"
            label="Results Description (3 lines)"
            rules={[{ required: true, message: 'Please enter results description' }]}
          >
            <TextArea rows={3} placeholder="Results description" />
          </Form.Item>
          <Form.Item
            name="result1Title"
            label="First Result Title"
          >
            <Input placeholder="First result title" />
          </Form.Item>
          <Form.Item
            name="result1Description"
            label="First Result Description (2 lines)"
          >
            <TextArea rows={2} placeholder="First result description" />
          </Form.Item>
          <Form.Item
            name="result2Title"
            label="Second Result Title"
          >
            <Input placeholder="Second result title" />
          </Form.Item>
          <Form.Item
            name="result2Description"
            label="Second Result Description (2 lines)"
          >
            <TextArea rows={2} placeholder="Second result description" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="image1"
                label="thumbnail (272x352)"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload Image 1');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'Image 1', 5, 272, 352)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload Image 1</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="image2"
                label="Image 2 (272x352) - Main Display"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload Image 2');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'Image 2', 5, 272, 352)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload Image 2</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="image3"
                label="Image 3 (577x352) - Secondary"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload Image 3');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'Image 3', 5, 577, 352)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload Image 3</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="image4"
                label="Image 4 (425x320) - Gallery"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload Image 4');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'Image 4', 5, 425, 320)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload Image 4</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="image5"
                label="Image 5 (425x320) - Gallery"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload Image 5');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'Image 5', 5, 425, 320)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload Image 5</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Set Homepage Position"
        visible={orderModalVisible}
        onOk={handleOrderUpdate}
        onCancel={() => setOrderModalVisible(false)}
      >
        {selectedProject && (
          <div>
            <p>Project: <strong>{selectedProject.title}</strong></p>
            <Select
              style={{ width: '100%' }}
              value={selectedProject.homepageOrder || 0}
              onChange={(value) => setSelectedProject({
                ...selectedProject,
                homepageOrder: value
              })}
            >
              <Select.Option value={0}>Not on homepage</Select.Option>
              <Select.Option value={1}>Position 1 (Featured)</Select.Option>
              <Select.Option value={2}>Position 2</Select.Option>
              <Select.Option value={3}>Position 3</Select.Option>
              <Select.Option value={4}>Position 4</Select.Option>
            </Select>
            <p style={{ marginTop: 16, color: '#666' }}>
              Only 4 projects can be featured on homepage. Selecting a position will replace any existing project in that spot.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Projects;